import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  Inject,
  OnInit,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { QuillModule } from 'ngx-quill';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ConnectiveservicesService } from '../../../../Services/Apis/Connectiveservices/connectiveservices.service';
import { environment } from '../../../../../environments/environment';
import { ApiservicesService } from '../../../../Services/Apis/apiservices.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ToastComponent } from '../../../../components/toast/toast.component';
import { ToastService } from '../../../../Services/toast/toast.service';
import { TranslateDirective } from '../../../../components/Utilites/Directives/translate.directive';
import { ConfigService } from '../../../../Services/configservices/sasTokenConfig';

@Component({
  selector: 'app-blogdialog',
  standalone: true,
  imports: [
    CommonModule,
    QuillModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    ToastComponent,
    TranslateDirective,
  ],
  templateUrl: './blogdialog.component.html',
  styleUrl: './blogdialog.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class BlogdialogComponent implements OnInit {
  private fb = inject(FormBuilder);
  private config = inject(ConfigService);
  private connectiveservices = inject(ConnectiveservicesService);
  private apiservice = inject(ApiservicesService);
  private toast = inject(ToastService);

  // State
  blogform: FormGroup;
  htmlText = '';
  subject: string = '';
  file: File | null | any = null;
  file1: any = null;
  publish: any = false;
  publish_dt: Date | null = null;
  selectedImageUrl: string | ArrayBuffer | null = null;

  // These will be updated dynamically from ConfigService
  containerName = environment.ASSET_CONTAINER;
  fileUrl: string | null = null;

  quillConfig = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline', 'strike'],
        ['code-block'],
        [{ header: 1 }, { header: 2 }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ color: [] }, { background: [] }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ direction: 'rtl' }],
        [{ size: ['small', false, 'large', 'huge'] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ font: [] }],
        [{ align: [] }],
        ['clean'],
        ['link', 'image', 'video'],
      ],
    },
    keyboard: {
      bindings: {
        enter: {
          key: 13,
          handler: (range: any, context: any) => {
            return true;
          },
        },
      },
    },
  };

  constructor(
    public dialogRef: MatDialogRef<BlogdialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.blogform = this.fb.group({
      blogimg: new FormControl('', [Validators.required]),
      title: new FormControl('', [Validators.required]),
      content: new FormControl('', [Validators.required]),
    });
  }

  ngOnInit(): void {
    if (this.data.action === 'ADD') {
      this.setdata();
    }
    if (this.data && this.data.action === 'EDIT') {
      this.setdata();
      this.blogform.controls['title'].disable();
      this.blogform.controls['blogimg'].disable();
    }
    if (this.data && this.data.action === 'VIEW') {
      this.blogform.disable();
      this.setdata();
    }
  }

  imageInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.file = input.files[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        this.selectedImageUrl = e.target?.result as string;
      };
      reader.readAsDataURL(this.file);
    } else {
      console.error('No file selected.');
    }
  }

  async submitDialog() {
    if (
      this.blogform.valid ||
      (this.data.action === 'EDIT' && this.blogform.get('content')?.valid)
    ) {
      try {
        // Ensure fresh token before upload
        await this.config.getStorageConnection();

        this.fileUrl = '';
        this.subject =
          this.blogform.controls['title']?.value || this.data.title;

        // Create clean text file name
        const cleanFileName = this.subject.split(' ').join('_') + '.txt';
        const blob = new Blob([this.htmlText], { type: 'text/plain' });
        const file = new File([blob], cleanFileName, { type: 'text/plain' });

        // Upload content file
        const urlWithSAS = await this.connectiveservices.uploadFile(
          file,
          Number(this.data.websiteId),
          'BLOGS',
        );
        const url = urlWithSAS.split('?')[0];
        const path = new URL(url).pathname;
        this.fileUrl = path.split('/').slice(2).join('/');
        this.toast.showToast('success', 'File uploaded successfully...!');

        let imageUrl = '';
        if (this.file) {
          const img = this.file.name.split('.')[0];
          const extension = this.file.name.split('.').pop();
          const newFileName = `${img}.${extension}`;
          const renamedFile = new File([this.file], newFileName, {
            type: this.file.type,
          });

          const imageUrlWithSAS = await this.connectiveservices.uploadFile(
            renamedFile,
            Number(this.data.websiteId),
            'BLOGS',
          );
          const imageUrlClean = imageUrlWithSAS.split('?')[0];
          const imagePath = new URL(imageUrlClean).pathname;
          imageUrl = imagePath.split('/').slice(2).join('/');
          this.toast.showToast('success', 'Image uploaded successfully...!');
        }

        const blogData = {
          title: this.subject,
          data_url: this.fileUrl,
          website_id: this.data.websiteId,
          website_page_id: this.data.pageId,
          image_url:
            imageUrl ||
            (this.data.action === 'EDIT' ? this.data.image_url : ''),
        };

        if (this.data.action === 'ADD') {
          this.apiservice.createBlog(blogData).subscribe({
            next: (res: any) => {
              if (res) this.dialogRef.close(res.status);
              this.toast.showToast('success', 'Blog Created successfully!');
            },
            error: (err: any) => {
              console.error('Creation failed:', err);
              this.toast.showToast('danger', 'Error Creating blog.');
            },
          });
        }

        if (this.data && this.data.action === 'EDIT') {
          this.apiservice.updateBlog(this.data.id, blogData).subscribe({
            next: (res: any) => {
              if (res) this.dialogRef.close(res.status);
              this.toast.showToast('success', 'Blog updated successfully!');
            },
            error: (err: any) => {
              console.error('Update failed:', err);
              this.toast.showToast('danger', 'Error updating blog.');
            },
          });
        }
      } catch (err) {
        console.error('File upload failed:', err);
        this.toast.showToast('danger', 'Error uploading file.');
      }
    } else {
      this.toast.showToast('danger', 'Please fill all required fields.');
    }
  }

  async setdata() {
    if (this.data.action === 'EDIT' || this.data.action === 'VIEW') {
      try {
        // Fetch fresh token so we can download content and see images
        const connectionString = await this.config.getStorageConnection();
        const baseUrl = connectionString.split('?')[0];

        this.apiservice
          .getdialogbyid(this.data.id)
          .subscribe(async (res: any) => {
            if (res.data_url) {
              // Fetch protected .txt file with dynamic SAS
              fetch(`${baseUrl}${this.containerName}/${res.data_url}`)
                .then((response) => response.text())
                .then((data) => {
                  this.htmlText = data;
                  this.blogform.get('content')?.setValue(this.htmlText);
                })
                .catch((error) =>
                  console.error('Error loading content:', error),
                );
            }

            this.subject = res.title;
            this.blogform.get('title')?.setValue(this.subject);
            this.publish = res.is_published == 1;
            this.publish_dt = res.created_at;

            if (res.image_url) {
              // Set preview image with dynamic SAS
              this.selectedImageUrl = `${baseUrl}${this.containerName}/${res.image_url}`;
            }
          });
      } catch (err) {
        console.error('Token fetch failed for dialog content', err);
      }
    }

    if (this.data.action === 'ADD') {
      this.blogform.get('content')?.setValue('');
      this.blogform.get('title')?.setValue('');
      this.selectedImageUrl = null;
    }
  }

  onContentChanged = (event: any) => (this.htmlText = event.html);

  onSelectionChanged = (event: any) => {
    if (event.oldRange == null) this.onFocus();
    if (event.range == null) this.onBlur();
  };

  onFocus = () => console.log('On Focus');
  onBlur = () => console.log('Blurred');
}
